import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "mv_admin_password";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const AdminPlaylist = () => {
  const [password, setPassword] = useState(
    () => sessionStorage.getItem(STORAGE_KEY) || ""
  );
  const [input, setInput] = useState("");
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(false);

  const authHeaders = { "X-Admin-Password": password };

  const fetchSongs = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/playlist`);
      setSongs(data);
    } catch {
      toast.error("No se pudo cargar la lista.");
    } finally {
      setLoading(false);
    }
  };

  const verifyPassword = async (pwd) => {
    // Ping the CSV endpoint with HEAD-like GET to confirm password works
    try {
      const res = await axios.get(`${API}/playlist/export.csv`, {
        headers: { "X-Admin-Password": pwd },
        responseType: "text",
      });
      return res.status === 200;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (!password) return;
    (async () => {
      const ok = await verifyPassword(password);
      if (!ok) {
        setAuthError(true);
        setPassword("");
        sessionStorage.removeItem(STORAGE_KEY);
      } else {
        fetchSongs();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const ok = await verifyPassword(input.trim());
    if (!ok) {
      setAuthError(true);
      toast.error("Contraseña incorrecta.");
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, input.trim());
    setPassword(input.trim());
    setAuthError(false);
    setInput("");
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPassword("");
    setSongs([]);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta canción?")) return;
    try {
      await axios.delete(`${API}/playlist/${id}`, { headers: authHeaders });
      setSongs((prev) => prev.filter((s) => s.id !== id));
      toast.success("Canción eliminada.");
    } catch {
      toast.error("No se pudo eliminar.");
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/playlist/export.csv`, {
        headers: authHeaders,
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "wedding-playlist.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo exportar el CSV.");
    }
  };

  if (!password) {
    return (
      <div className="min-h-screen bg-[#ECE7DC] text-[#0a0a0a]" data-testid="admin-login">
        <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-24">
          <p className="corner-label text-[#8a8580]">Área privada</p>
          <h1 className="mt-4 display-xl">Admin.</h1>
          <p className="mt-4 body-lead">
            Introduce la contraseña para gestionar la playlist de la boda.
          </p>
          <form onSubmit={handleLogin} className="mt-10">
            <label htmlFor="admin-pwd" className="corner-label text-[#0a0a0a]">
              Contraseña
            </label>
            <input
              id="admin-pwd"
              data-testid="admin-password"
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="mt-2 block w-full border-b border-[#c9c3b3] bg-transparent py-2 text-lg outline-none transition focus:border-[#0a0a0a]"
              autoFocus
            />
            {authError && (
              <p className="mt-2 text-sm text-[#891613]">Contraseña incorrecta.</p>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit"
              className="btn-base btn-primary mt-8"
            >
              <span>Entrar</span>
              <span aria-hidden="true">→</span>
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#ECE7DC] text-[#0a0a0a]" data-testid="admin-playlist">
      <div className="mx-auto max-w-[1200px] px-6 py-16 md:px-10">
        <div className="flex items-start justify-between">
          <div>
            <p className="corner-label text-[#8a8580]">Área privada</p>
            <h1 className="mt-3 display-xl">Playlist admin.</h1>
            <div className="mt-4 flex items-center gap-4">
              <Link to="/admin/rsvp" className="corner-label nav-underline">
                RSVP
              </Link>
              <span className="corner-label text-[#8a8580]">·</span>
              <Link to="/admin/playlist" className="corner-label nav-underline">
                Playlist
              </Link>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="corner-label nav-underline"
          >
            Cerrar sesión
          </button>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <button
            onClick={handleExport}
            data-testid="admin-export"
            className="btn-base btn-primary"
          >
            <span>Exportar CSV</span>
            <span aria-hidden="true">↓</span>
          </button>
          <button
            onClick={fetchSongs}
            data-testid="admin-refresh"
            className="corner-label nav-underline"
          >
            Actualizar
          </button>
          <p className="ml-auto corner-label text-[#8a8580]">
            Total: {String(songs.length).padStart(2, "0")}
          </p>
        </div>

        <div className="mt-8 border-t border-[#c9c3b3]">
          {loading ? (
            <p className="py-8 body-lead text-[#8a8580]">Cargando…</p>
          ) : songs.length === 0 ? (
            <p className="py-8 body-lead text-[#8a8580]">Aún no hay canciones.</p>
          ) : (
            <ul className="divide-y divide-[#c9c3b3]">
              {songs.map((s) => (
                <li
                  key={s.id}
                  className="py-5"
                  data-testid={`admin-item-${s.id}`}
                >
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="display-md leading-tight">{s.song_title}</p>
                      <p className="mt-1 body-lead text-[#3a3a3a]">
                        {s.artist}
                      </p>
                      {s.message && (
                        <p className="mt-2 italic-serif text-[#3a3a3a]">
                          "{s.message}"
                        </p>
                      )}
                      <p className="mt-2 corner-label text-[#8a8580]">
                        {formatDate(s.created_at)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDelete(s.id)}
                      data-testid={`admin-delete-${s.id}`}
                      className="self-start corner-label text-[#891613] nav-underline"
                    >
                      Eliminar
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPlaylist;
